import { useState, useCallback } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../Keycloak/useAuthenticatedFetchWithWorkspace";
import { Store } from "../../types/Store";
import { useStoreCache } from "../../context/StoreCacheContext";
import { useStoreList } from "../../context/StoreListContext";

export interface AddStorePayload {
    currency_id: string;
    identifier: string;
    name: string;
    entity_type: string;
    subscribe_date?: string;
    unsubscribe_date?: string;
    franchise: string;
    signature?: string;
    entity_number?: string;
    latitude?: string;
    longitude?: string;
    transaction_integration_mode?: string;
    opening_hours?: string;
    description?: string;
    payment_overdue?: string;
    address1?: string;
    address2?: string;
    address3_type?: string;
    address3_num?: string;
    address3_name?: string;
    address4?: string;
    zip_code?: string;
    local_city_name?: string;
    short_city_name?: string;
    local_country_name?: string;
    phone_number?: string;
    fax_number?: string;
    dialing_code1?: string;
    dialing_code2?: string;
    email_address?: string;
    add_depend_on?: string;
}

interface UseAddStoreReturn {
    addStore: (payload: AddStorePayload) => Promise<Store | null>;
    loading: boolean;
    error: string;
    success: boolean;
    data: Store | null;
    reset: () => void;
}

const useAddStore = (): UseAddStoreReturn => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const { addToCache } = useStoreCache();
    const { refreshStores } = useStoreList();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [success, setSuccess] = useState(false);
    const [data, setData] = useState<Store | null>(null);

    const addStore = useCallback(async (payload: AddStorePayload): Promise<Store | null> => {
        if (!isReady) {
            setError("Workspace not ready");
            return null;
        }

        try {
            setLoading(true);
            setError("");
            setSuccess(false);
            setData(null);

            const result: Store = await authFetch(`/addStore`, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/ld+json',
                    'Accept': 'application/ld+json'
                },
                body: JSON.stringify(payload),
            });

            if (!result) throw new Error("Failed to create store.");

            // Ajouter le nouveau store au cache individuel
            if (result.ENTITY_ID) {
                addToCache(result.ENTITY_ID, result);
            }
            // Rafraîchir la liste partagée
            refreshStores(true);

            setData(result);
            setSuccess(true);
            return result;
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError("An unknown error occurred while creating the store");
            }
            return null;
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady, addToCache, refreshStores]);

    const reset = useCallback(() => {
        setLoading(false);
        setError("");
        setSuccess(false);
        setData(null);
    }, []);

    return { addStore, loading, error, success, data, reset };
};

export default useAddStore;
